import React from "react";
import {
  MdDragIndicator,
  MdEdit,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { IconButton } from "components/button";
import Badge from "components/badge";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * One draggable entry in the Action Buttons list. The whole row is the drag source;
 * the handle also reorders with ArrowUp/ArrowDown for keyboard users.
 */
const ButtonOrderRow = ({
  title,
  subtitle,
  swatchColor,
  visible,
  dragging,
  isFirst,
  isLast,
  editing,
  editLabel,
  editPanelId,
  onDragStart,
  onDragOver,
  onDragEnd,
  onMove,
  onToggleVisibility,
  onToggleEdit,
  children,
}) => {
  const handleHandleKeyDown = (e) => {
    if (e.key === "ArrowUp" && !isFirst) {
      e.preventDefault();
      onMove("up");
    } else if (e.key === "ArrowDown" && !isLast) {
      e.preventDefault();
      onMove("down");
    }
  };

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cx("p-3 transition-colors", dragging && "bg-surface-subtle opacity-50")}
    >
      <div className="flex items-center gap-3">
        {/* A span, not a <button>: Firefox will not start a drag from a button. */}
        <span
          role="button"
          tabIndex={0}
          aria-label={`Reorder “${title}”`}
          aria-keyshortcuts="ArrowUp ArrowDown"
          title="Drag, or use the arrow keys, to reorder"
          onKeyDown={handleHandleKeyDown}
          className="focus-ring flex h-8 w-6 shrink-0 cursor-grab items-center justify-center rounded-control text-fg-muted hover:text-fg active:cursor-grabbing"
        >
          <MdDragIndicator aria-hidden="true" className="h-5 w-5" />
        </span>
        <div className={cx("flex min-w-0 flex-1 items-center gap-3", !visible && "opacity-60")}>
          <span
            aria-hidden="true"
            className="h-7 w-7 shrink-0 rounded-full border border-line"
            style={{ backgroundColor: swatchColor }}
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate font-medium text-fg">{title}</p>
              {!visible && <Badge tone="neutral">Hidden</Badge>}
            </div>
            <p className="truncate text-xs text-fg-muted">{subtitle}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IconButton
            size="sm"
            icon={visible ? <MdVisibility /> : <MdVisibilityOff />}
            label={visible ? `Hide “${title}”` : `Show “${title}”`}
            onClick={onToggleVisibility}
          />
          <IconButton
            size="sm"
            icon={<MdEdit />}
            label={editLabel}
            aria-expanded={editing}
            aria-controls={editing ? editPanelId : undefined}
            onClick={onToggleEdit}
            className={editing ? "bg-surface-subtle" : undefined}
          />
          <IconButton
            size="sm"
            icon={<MdKeyboardArrowUp />}
            label={`Move “${title}” up`}
            disabled={isFirst}
            onClick={() => onMove("up")}
          />
          <IconButton
            size="sm"
            icon={<MdKeyboardArrowDown />}
            label={`Move “${title}” down`}
            disabled={isLast}
            onClick={() => onMove("down")}
          />
        </div>
      </div>
      {editing && (
        <div id={editPanelId} className="mt-3 flex flex-col gap-3 border-t border-line pt-3">
          {children}
        </div>
      )}
    </li>
  );
};

export default ButtonOrderRow;
